import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledInput from 'src/components/form/controlled-input';
import { ControlledSwitch } from 'src/components/form/controlled-switch/ControlledSwitch';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { FormField } from 'src/components/form/form/FormField';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';

import { emptyFilterQuery } from '@/utils/collectionUtils';

import type { WidgetDataSource } from '../../gigawidget/types';
import { WidgetPropertyFilter } from '../../universal-widget/form/WidgetPropertyFilter';
import type { IndicatorResultsOverTimeSettings } from './indicatorResultsOverTimeSettingsSchema';
import { indicatorResultsOverTimeSettingsSchema } from './indicatorResultsOverTimeSettingsSchema';

type FieldsProps<TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
  /** Only surface the tolerances toggle when the widget shows a single indicator with reference values. */
  showTolerancesToggle: boolean;
};

const IndicatorResultsOverTimeSettingsFormFields = <
  TDataSource extends WidgetDataSource,
>({
  dataSource,
  showTolerancesToggle,
}: FieldsProps<TDataSource>) => {
  const { control, watch } = useFormContext<IndicatorResultsOverTimeSettings>();
  const { t } = useTranslation('common', {
    keyPrefix: 'dashboard.widgetSettings.fields',
  });

  const customTitle = watch('customTitle');

  return (
    <div>
      <Controller
        control={control}
        render={({ field: { value, onChange } }) => (
          <>
            <FormField label={t('filtering')}>
              <WidgetPropertyFilter
                value={value}
                onChange={onChange}
                dataSource={dataSource}
              />
            </FormField>
            <div className={'mb-4'}>
              <ControlledSwitch
                name={'customTitle'}
                label={t('customTitle')}
                control={control}
              />
            </div>
            {customTitle && (
              <ControlledInput
                control={control}
                name={'title'}
                label={t('customTitle')}
              />
            )}
            {showTolerancesToggle && (
              <div className={'mb-4'}>
                <ControlledSwitch
                  name={'showTolerances'}
                  label={t('showTolerances')}
                  control={control}
                  testId={'showTolerances'}
                />
              </div>
            )}
          </>
        )}
        name={'filtering'}
      />
    </div>
  );
};

type Props<TDataSource extends WidgetDataSource> = {
  onDismiss: () => void;
  onSave: (data: IndicatorResultsOverTimeSettings) => Promise<void>;
  dataSource: TDataSource;
  showTolerancesToggle: boolean;
};

export const IndicatorResultsOverTimeSettingsModal = <
  TDataSource extends WidgetDataSource,
>({
  onDismiss,
  onSave,
  dataSource,
  showTolerancesToggle,
}: Props<TDataSource>) => {
  const { t } = useTranslation('common');
  const [settings] =
    useDashboardWidgetSettings<IndicatorResultsOverTimeSettings>();

  const values: IndicatorResultsOverTimeSettings = {
    filtering: emptyFilterQuery,
    customTitle: false,
    showTolerances: false,
    ...(settings ?? {}),
  };

  return (
    <CustomisableForm
      onSave={onSave}
      defaultValues={values}
      formId={'indicator-results-over-time-form'}
      schema={indicatorResultsOverTimeSettingsSchema}
      i18n={t('widget')}
      renderTemplate={(renderProps) => (
        <ModalWrapper {...renderProps} visible={true} />
      )}
      onDismiss={onDismiss}
    >
      <IndicatorResultsOverTimeSettingsFormFields
        dataSource={dataSource}
        showTolerancesToggle={showTolerancesToggle}
      />
    </CustomisableForm>
  );
};
